import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Box, Typography, TextField, Button, Paper, Link, Stepper, Step, StepLabel, Alert, Checkbox } from '@mui/material';
import Select from 'react-select';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { countries } from 'countries-list';
import { API_URL } from '../apiConfig';

const countryOptions = Object.entries(countries)
  .map(([code, country]) => ({
    value: code,
    label: country.name,
    code: country.phone,
    flag: code.toLowerCase(),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const Auth = ({ setAuth }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isMfaStep, setIsMfaStep] = useState(false);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationNumber, setOrganizationNumber] = useState('');
  const [country, setCountry] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resetPasswordMessage, setResetPasswordMessage] = useState('');
  const [passwordResetLinkSent, setPasswordResetLinkSent] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [isRegistrationPaused, setIsRegistrationPaused] = useState(false);

  const steps = ['Account Info', 'Contact Info', 'Company Info', 'Terms'];

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/get-csrf-token`, { withCredentials: true });
        axios.defaults.headers.common['X-CSRFToken'] = response.data.csrf_token;
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };

    const checkRegistrationStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/is_registration_paused`);
        setIsRegistrationPaused(response.data.registration_paused);
      } catch (error) {
        console.error('Error checking registration status:', error);
      }
    };

    fetchCsrfToken();
    checkRegistrationStatus();
  }, []);

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const handleNext = () => {
    if (step === 0) {
      if (!email || !password || !confirmPassword) {
        setErrorMessage('Please fill in all required fields.');
        return;
      }
      if (email.length > 254) {
        setErrorMessage('Email must be 254 characters or fewer.');
        return;
      }
      if (!validateEmail(email)) {
        setErrorMessage('Please enter a valid email address.');
        return;
      }
      if (password.length > 50) {
        setErrorMessage('Password must be 50 characters or fewer.');
        return;
      }
      if (confirmPassword.length > 50) {
        setErrorMessage('Confirm Password must be 50 characters or fewer.');
        return;
      }
    } else if (step === 1) {
      if (!companyName || !contactPerson) {
        setErrorMessage('Please fill in all required fields.');
        return;
      }
      if (companyName.length > 100) {
        setErrorMessage('Company name must be 100 characters or fewer.');
        return;
      }
      if (contactPerson.length > 100) {
        setErrorMessage('Contact person name must be 100 characters or fewer.');
        return;
      }
    } else if (step === 2) {
      if (!organizationName || !organizationNumber || !country || !phoneNumber) {
        setErrorMessage('Please fill in all required fields.');
        return;
      }
      if (organizationName.length > 100) {
        setErrorMessage('Organization name must be 100 characters or fewer.');
        return;
      }
      if (organizationNumber.length > 20) {
        setErrorMessage('Organization number must be 20 characters or fewer.');
        return;
      }
      if (country.label.length > 56) {
        setErrorMessage('Country name must be 56 characters or fewer.');
        return;
      }
      if (phoneNumber.length > 20) {
        setErrorMessage('Phone number must be 20 characters or fewer.');
        return;
      }
    } else if (step === 3 && !acceptTerms) {
      setErrorMessage('You must accept the terms and conditions to proceed.');
      return;
    }
    setErrorMessage('');
    setStep((prevStep) => prevStep + 1);
  };

  const validatePassword = (pass) => {
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!@#$%^&*()_+[\]{};':"\\|,.<>/?~`-]{12,}$/;
    return passwordRegex.test(pass);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (!validatePassword(newPassword)) {
      setPasswordError('Password must be at least 12 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.');
    } else if (newPassword.length > 50) {
      setPasswordError('Password must be 50 characters or fewer.');
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const confirmPass = e.target.value;
    setConfirmPassword(confirmPass);
    if (confirmPass !== password) {
      setPasswordError('Passwords do not match.');
    } else if (confirmPass.length > 50) {
      setPasswordError('Confirm Password must be 50 characters or fewer.');
    } else {
      setPasswordError('');
    }
  };

  const handleAuth = async () => {
    if (!validateEmail(email) && !isLogin) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    if (!validatePassword(password) && !isLogin) {
      setPasswordError('Password must be at least 12 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.');
      return;
    }
    if (password !== confirmPassword && !isLogin) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordError('');

    const endpoint = isLogin ? '/api/login' : '/api/register';
    let data;

    if (isLogin) {
      data = {
        email,
        password,
      };
    } else {
      data = {
        email,
        company_name: companyName,
        contact_person: contactPerson,
        organization_name: organizationName,
        organization_number: organizationNumber,
        country: country?.label || '',
        phone_number: phoneNumber,
        password,
        privacy_policy_accepted: acceptTerms,
        terms_of_service_accepted: acceptTerms
      };
    }

    try {
      const response = await axios.post(`${API_URL}${endpoint}`, data, {
        withCredentials: true
      });
      if (isLogin) {
        if (response.data.use_mfa) {
          setIsMfaStep(true);
        } else {
          localStorage.setItem('accessToken', response.data.access_token);
          setAuth(true);
        }
      } else {
        alert('User registered successfully');
        setIsLogin(true);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        if (error.response.data.message.includes('CSRF token has expired')) {
          alert('Session expired, please refresh the page.');
        } else {
          setErrorMessage(error.response.data.message);
        }
      } else {
        setErrorMessage('Oops, session token has expired! Please refresh the page and try again.');      }
    }
  };

  const handleVerifyMfa = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/verify_mfa_code`, { email, code: mfaCode }, {
        withCredentials: true
      });

      localStorage.setItem('accessToken', response.data.access_token);
      setAuth(true);
    } catch (error) {
      setErrorMessage('Invalid or expired MFA code');
    }
  };

  const handleCountryChange = (selectedOption) => {
    setCountry(selectedOption);
    setPhoneNumber(`+${selectedOption.code}`);
  };

  const handlePhoneChange = (phone, country) => {
    const countryCode = country?.dialCode || '';
    const formattedPhone = `+${countryCode} ${phone.slice(countryCode.length)}`;
    setPhoneNumber(formattedPhone);
  };

  const handlePasswordReset = async () => {
    try {
      const endpoint = isTeamMember ? '/api/request_team_member_password_reset' : '/api/request_password_reset';
      const response = await axios.post(`${API_URL}${endpoint}`, { email }, {
        withCredentials: true
      });
      setResetPasswordMessage(response.data.message);
      setPasswordResetLinkSent(true);
      setIsForgotPassword(false);
      setIsLogin(true);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setResetPasswordMessage('An error occurred. Please try again later.');
    }
  };

  const openPopup = (url) => {
    window.open(url, 'popup', 'width=600,height=600');
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ mt: 8, p: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography component="h1" variant="h5">
            {isLogin ? 'Login' : isForgotPassword ? 'Reset Password' : 'Register'}
          </Typography>
          {errorMessage && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {(resetPasswordMessage && (isLogin || passwordResetLinkSent)) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {resetPasswordMessage}
            </Alert>
          )}
          {isRegistrationPaused && !isLogin && !isForgotPassword && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Registration is temporarily closed. Please contact <a href="mailto:contact@archiveme.ai">contact@archiveme.ai</a> for any inquiries.
            </Alert>
          )}
          {!isRegistrationPaused && !isLogin && !isForgotPassword && (
            <Stepper activeStep={step} alternativeLabel sx={{ width: '100%', mt: 3, mb: 5 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
          <Box component="form" onSubmit={e => { e.preventDefault(); isForgotPassword ? handlePasswordReset() : isMfaStep ? handleVerifyMfa() : handleAuth(); }} sx={{ mt: 1 }}>
            {isForgotPassword ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={email.length > 254}
                  helperText={email.length > 254 && "Email must be 254 characters or fewer."}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, mb: 2 }}
                >
                  Send Password Reset Link
                </Button>
                <Link href="#" variant="body2" onClick={() => { setIsForgotPassword(false); setErrorMessage(''); setResetPasswordMessage(''); }}>
                  Back to Login
                </Link>
              </>
            ) : isMfaStep ? (
              <>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Check your email for the MFA code, it's a 6 digit number.
                </Alert>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="mfaCode"
                  label="MFA Code"
                  name="mfaCode"
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, mb: 2 }}
                >
                  Verify MFA Code
                </Button>
              </>
            ) : isLogin ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={email.length > 254 || !validateEmail(email)}
                  helperText={
                    email.length > 254
                      ? "Email must be 254 characters or fewer."
                      : !validateEmail(email) && "Please enter a valid email address."
                  }
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={handlePasswordChange}
                  error={password.length > 50}
                  helperText={password.length > 50 && "Password must be 50 characters or fewer."}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, mb: 2 }}
                >
                  Login
                </Button>
                <Link href="#" variant="body2" onClick={() => { setIsForgotPassword(true); setErrorMessage(''); setResetPasswordMessage(''); }}>
                  Forgot password?
                </Link>
              </>
            ) : (
              !isRegistrationPaused && (
                <>
                  {step === 0 && (
                    <>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={email.length > 254 || !validateEmail(email)}
                        helperText={
                          email.length > 254
                            ? "Email must be 254 characters or fewer."
                            : !validateEmail(email) && "Please enter a valid email address."
                        }
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={handlePasswordChange}
                        error={password.length > 50}
                        helperText={password.length > 50 && "Password must be 50 characters or fewer."}
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm Password"
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        error={confirmPassword.length > 50}
                        helperText={confirmPassword.length > 50 && "Confirm Password must be 50 characters or fewer."}
                      />
                      {passwordError && (
                        <Typography color="error" variant="body2">
                          {passwordError}
                        </Typography>
                      )}
                    </>
                  )}
                  {step === 1 && (
                    <>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="companyName"
                        label="Company Name"
                        name="companyName"
                        autoComplete="companyName"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        error={companyName.length > 100}
                        helperText={companyName.length > 100 ? "Company name must be 100 characters or fewer." : "Example: TechCorp. (Informal or brand name)"}
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="contactPerson"
                        label="Contact Person"
                        name="contactPerson"
                        autoComplete="contactPerson"
                        value={contactPerson}
                        onChange={(e) => setContactPerson(e.target.value)}
                        error={contactPerson.length > 50}
                        helperText={contactPerson.length > 50 && "Contact person name must be 50 characters or fewer."}
                      />
                    </>
                  )}
                  {step === 2 && (
                    <>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="organizationName"
                        label="Organization Name"
                        name="organizationName"
                        autoComplete="organizationName"
                        value={organizationName}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        error={organizationName.length > 100}
                        helperText={organizationName.length > 100 ? "Organization name must be 100 characters or fewer." : "Example: 'TechCorp LLC' (Legally registered name)"}
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="organizationNumber"
                        label="Organization Number"
                        name="organizationNumber"
                        autoComplete="organizationNumber"
                        value={organizationNumber}
                        onChange={(e) => setOrganizationNumber(e.target.value)}
                        error={organizationNumber.length > 20}
                        helperText={organizationNumber.length > 20 && "Organization number must be 20 characters or fewer."}
                      />
                      <Select
                        options={countryOptions}
                        onChange={handleCountryChange}
                        placeholder="Select a country"
                        value={country}
                        getOptionLabel={(option) => (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span className={`flag-icon flag-icon-${option.flag}`} style={{ marginRight: 10 }}></span>
                            {option.label}
                          </div>
                        )}
                        getOptionValue={(option) => option.value}
                        styles={{ container: (provided) => ({ ...provided, marginBottom: '16px' }) }}
                      />
                      <PhoneInput
                        country={country ? country.value.toLowerCase() : ''}
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        inputProps={{
                          name: 'phone',
                          required: true,
                          autoFocus: false,
                        }}
                        specialLabel="Phone Number"
                        error={phoneNumber.length > 20}
                        helperText={phoneNumber.length > 20 && "Phone number must be 20 characters or fewer."}
                      />
                    </>
                  )}
                  {step === 3 && (
                    <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
                      <Checkbox
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        color="primary"
                      />
                      <Typography variant="body2">
                        I have read and accept the
                        <Link href="#" onClick={() => openPopup('/terms_and_conditions.html')}> Terms of Service </Link> and
                        <Link href="#" onClick={() => openPopup('/privacy.html')}> Privacy Policy</Link>.
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                      disabled={step === 0}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                    {step === steps.length - 1 ? (
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ mt: 1, mr: 1 }}
                        disabled={!acceptTerms}
                      >
                        Register
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </>
              )
            )}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
              <Typography variant="body2">
                {isLogin || isForgotPassword ? "Don't have an account?" : "Already have an account?"}
              </Typography>
              <Link
                href="#"
                variant="body2"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setIsForgotPassword(false);
                  setStep(0);
                  setErrorMessage('');
                  setPasswordError('');
                  setResetPasswordMessage('');
                  setPasswordResetLinkSent(false);
                }}
              >
                {isLogin || isForgotPassword ? 'Register' : 'Login'}
              </Link>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Auth;
